"use client"

/**
 * Animated SVG backdrop for the login page.
 * Layers:
 *  1. Two large radial glow orbs (handled in parent for color richness)
 *  2. A subtle PCB-style dot grid that twinkles
 *  3. Animated "power flow" paths that trace across the canvas
 *  4. Scanning beam that sweeps slowly top-to-bottom
 * Purely CSS / SMIL animations — no JS dependencies.
 */
export function LoginBackground() {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 1200 900"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Vignette */}
        <radialGradient id="vignette" cx="50%" cy="40%" r="80%">
          <stop offset="0%" stopColor="rgba(0,0,0,0)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.55)" />
        </radialGradient>

        {/* Grid pattern (dots) */}
        <pattern id="dot-grid" width="38" height="38" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill="rgba(0,229,160,0.18)" />
        </pattern>

        {/* Power line gradient */}
        <linearGradient id="power-flow" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(0,229,160,0)" />
          <stop offset="50%" stopColor="rgba(0,229,160,0.9)" />
          <stop offset="100%" stopColor="rgba(0,229,160,0)" />
        </linearGradient>

        {/* Scan beam */}
        <linearGradient id="scan-beam" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(0,229,160,0)" />
          <stop offset="50%" stopColor="rgba(0,229,160,0.15)" />
          <stop offset="100%" stopColor="rgba(0,229,160,0)" />
        </linearGradient>

        {/* Soft glow filter */}
        <filter id="bg-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>

      {/* Dot grid */}
      <rect width="1200" height="900" fill="url(#dot-grid)" opacity="0.55" />

      {/* Twinkling accent dots */}
      {[
        [120, 180, 0],
        [320, 90, 1.2],
        [880, 230, 0.4],
        [1080, 520, 2.1],
        [240, 680, 1.6],
        [620, 800, 0.8],
        [780, 580, 2.5],
        [180, 460, 3.2],
      ].map(([cx, cy, delay], i) => (
        <circle key={i} cx={cx} cy={cy} r="2.4" fill="#00E5A0" filter="url(#bg-glow)">
          <animate
            attributeName="opacity"
            values="0;1;0"
            dur="3.6s"
            begin={`${delay}s`}
            repeatCount="indefinite"
          />
        </circle>
      ))}

      {/* Power flow paths — traveling pulses along PCB-like routes */}
      <g stroke="rgba(255,255,255,0.04)" strokeWidth="1" fill="none">
        <path d="M0 240 H 380 L 460 320 H 760 L 840 240 H 1200" />
        <path d="M0 620 H 220 L 300 540 H 540 L 620 620 H 920 L 1000 700 H 1200" />
        <path d="M0 420 H 140 L 220 360 H 1200" />
      </g>

      {/* Traveling pulses (clipped to invisible reveal mask via gradient stroke) */}
      <g fill="none" strokeWidth="2" strokeLinecap="round" filter="url(#bg-glow)">
        <path
          d="M0 240 H 380 L 460 320 H 760 L 840 240 H 1200"
          stroke="url(#power-flow)"
          strokeDasharray="120 1200"
        >
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-1320"
            dur="6s"
            repeatCount="indefinite"
          />
        </path>
        <path
          d="M0 620 H 220 L 300 540 H 540 L 620 620 H 920 L 1000 700 H 1200"
          stroke="url(#power-flow)"
          strokeDasharray="100 1400"
        >
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-1500"
            dur="7.5s"
            begin="2s"
            repeatCount="indefinite"
          />
        </path>
        <path
          d="M0 420 H 140 L 220 360 H 1200"
          stroke="url(#power-flow)"
          strokeDasharray="80 1300"
        >
          <animate
            attributeName="stroke-dashoffset"
            from="0"
            to="-1400"
            dur="9s"
            begin="4s"
            repeatCount="indefinite"
          />
        </path>
      </g>

      {/* Distant hex outlines (architecture motif) */}
      <g stroke="rgba(180,123,255,0.10)" strokeWidth="0.8" fill="none">
        <polygon points="940,80 1010,120 1010,200 940,240 870,200 870,120" />
        <polygon points="120,760 180,795 180,865 120,900 60,865 60,795" />
        <polygon points="300,160 340,183 340,229 300,252 260,229 260,183" />
      </g>

      {/* Vertical scanning beam */}
      <rect x="0" y="0" width="1200" height="160" fill="url(#scan-beam)">
        <animate attributeName="y" from="-160" to="900" dur="9s" repeatCount="indefinite" />
      </rect>

      {/* Vignette overlay (on top so corners darken) */}
      <rect width="1200" height="900" fill="url(#vignette)" />
    </svg>
  )
}
